import { useState, useRef } from "react";
import { Upload, X, Loader2, GripVertical } from "lucide-react";
import { uploadImage } from "@/lib/api";

// Componente per upload multiplo con drag-and-drop.
// Props:
//   images: array [{image_url, sort_order}]
//   onChange: (nuovoArray) => void
//   maxFiles: number opzionale
export default function ImageUploader({ images = [], onChange, maxFiles = 20 }) {
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const dragIndex = useRef(null);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setErrors([]);
    setUploading(true);

    const remaining = maxFiles - images.length;
    const toUpload = Array.from(files).slice(0, remaining);
    const uploaded = [];
    const localErrors = [];

    for (const file of toUpload) {
      try {
        const res = await uploadImage(file);
        uploaded.push({
          image_url: res.url,
          sort_order: images.length + uploaded.length,
        });
      } catch (err) {
        localErrors.push(`${file.name}: ${err.message}`);
      }
    }

    if (uploaded.length > 0) {
      onChange([...images, ...uploaded]);
    }
    if (localErrors.length > 0) setErrors(localErrors);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeImage = (idx) => {
    const next = images.filter((_, i) => i !== idx).map((img, i) => ({ ...img, sort_order: i }));
    onChange(next);
  };

  const onDragStart = (idx) => {
    dragIndex.current = idx;
  };

  const onDragOverItem = (e, idx) => {
    e.preventDefault();
    if (dragIndex.current === null || dragIndex.current === idx) return;
  };

  const onDropItem = (e, idx) => {
    e.preventDefault();
    const from = dragIndex.current;
    if (from === null || from === idx) return;
    const next = [...images];
    const [moved] = next.splice(from, 1);
    next.splice(idx, 0, moved);
    onChange(next.map((img, i) => ({ ...img, sort_order: i })));
    dragIndex.current = null;
  };

  const onZoneDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    // Solo se non stiamo droppando su una card (reorder)
    if (dragIndex.current !== null) return;
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div className="space-y-3">
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (dragIndex.current === null) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onZoneDrop}
        className={`border-2 border-dashed rounded-lg p-6 cursor-pointer text-center transition-colors ${
          dragOver ? "border-[#007AFF] bg-blue-50" : "border-[#E2E8F0] hover:border-[#94A3B8]"
        } ${uploading ? "opacity-60 pointer-events-none" : ""}`}
        data-testid="image-uploader-zone"
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-2 text-[#64748B]">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="text-sm">Caricamento…</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-[#64748B]">
            <Upload className="w-6 h-6" />
            <span className="text-sm font-medium">Trascina immagini qui, oppure clicca per selezionare</span>
            <span className="text-xs">JPEG, PNG, WEBP — max 5 MB per file</span>
          </div>
        )}
      </div>

      {errors.length > 0 && (
        <div className="p-3 rounded-lg bg-red-50 text-red-700 text-xs border border-red-200 space-y-1">
          {errors.map((e, i) => (
            <div key={i}>{e}</div>
          ))}
        </div>
      )}

      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
          {images.map((img, idx) => (
            <div
              key={idx}
              draggable
              onDragStart={() => onDragStart(idx)}
              onDragOver={(e) => onDragOverItem(e, idx)}
              onDrop={(e) => onDropItem(e, idx)}
              className="relative group aspect-square rounded-lg overflow-hidden border border-[#E2E8F0] bg-white cursor-move"
              data-testid={`image-preview-${idx}`}
            >
              <img
                src={img.image_url}
                alt=""
                className="w-full h-full object-cover"
                draggable={false}
              />
              <div className="absolute top-1 left-1 p-1 rounded bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                <GripVertical className="w-3 h-3" />
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeImage(idx);
                }}
                className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white hover:bg-red-600 transition-colors"
                aria-label="Rimuovi"
              >
                <X className="w-3 h-3" />
              </button>
              <div className="absolute bottom-1 left-1 text-xs bg-black/60 text-white px-1.5 py-0.5 rounded">
                {idx + 1}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
